import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AlertInfo, AlertService, AlertType } from '../Alert/alert.service';
import { BaseService } from '../base.service';

@Injectable({
  providedIn: 'root',
})
export class WorkstationService extends BaseService {
  constructor(
    private http: HttpClient,
    alertService: AlertService
  ) {
    super('workstation', alertService);
  }

  findAvailable(
    userId: number,
    startDate: Date,
    endDate: Date,
    recurrence: number,
    timeId: number,
    floorIds: number[],
    pageNumber = 0,
    pageSize = 10,
    isRepeat = false
  ): Observable<any> {
    var params = new HttpParams();

    // Only append params that hold real values. HttpParams.set() serializes
    // a nullish value as the literal string "undefined"/"null" onto the
    // query string, which fails .NET model binding on the required
    // primitive parameters and causes HTTP 400 before the action runs.
    if (userId != null) {
      params = params.set('userId', userId.toString());
    }
    if (startDate != null) {
      params = params.set('startDate', startDate.toDateString());
    }

    // endDate and recurrence are only meaningful for recurring bookings; if
    // the user toggles Repeat off after picking an endDate, the value
    // lingers in the form state (the control is `[disabled]`, not hidden).
    // Gate both on `isRepeat` so stale values can never round-trip into
    // backend `endDate.Value < startDate` 400s on a non-recurring search.
    if (isRepeat && endDate != null) {
      params = params.set('endDate', endDate.toDateString());
    }
    if (isRepeat && recurrence != null && recurrence > 0) {
      params = params.set('recurrence', recurrence.toString());
    }

    if (timeId != null) {
      params = params.set('timeId', timeId.toString());
    }

    if (floorIds != null && floorIds.length > 0) {
      params = params.set('floorIds', floorIds.toString());
    }

    params = params.set('pageNumber', pageNumber.toString());
    params = params.set('pageSize', pageSize.toString());
    params = params.set('isRepeat', isRepeat.toString());

    return this.http.get(`${this.url}/available`, { params: params }).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Getting Available Workstations', err))
    );
  }

  addIssue(obj: any) {
    const url = this.url + '/issue';
    return this.http.post(url, obj).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Submitting Report', err))
    );
  }

  getWorkstations(floorId: number, pageNumber: number, pageSize: number): Observable<any> {
    var params = new HttpParams();
    params = params.set('pageNumber', pageNumber.toString());
    params = params.set('pageSize', pageSize.toString());

    const url = this.url + '/floor/' + floorId + '/';
    return this.http.get(url, { params: params }).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Getting Workstations for Floor', err))
    );
  }

  getWorkstation(id: number): Observable<any> {
    const url = this.url + '/' + id;
    return this.http.get(url).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Getting Workstation', err))
    );
  }

  addWorkstation(workstation: any): Observable<any> {
    return this.http.post(this.url, workstation).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Creating workstation', err))
    );
  }

  update(id: number, body: any): Observable<any> {
    const url = this.url + '/' + id;
    return this.http.patch(url, body).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error updating workstation', err))
    );
  }

  remove(id: number) {
    const url = this.url + '/' + id;
    return this.http.delete(url).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error deleting workstation', err))
    );
  }

  disable(schedule: any): Observable<any> {
    const url = this.url + '/disable';
    return this.http.post(url, schedule).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Disabling Workstation', err))
    );
  }

  enable(id: number): Observable<any> {
    const url = this.url + '/enable/' + id;
    return this.http.post(url, null).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Enabling Workstation', err))
    );
  }

  hasUpcommingReservations(id: number): Observable<any> {
    const url = this.url + '/upcommingReservations/' + id;
    return this.http.get(url).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Getting Workstation Bookings', err))
    );
  }

  createFromFile(floorId: number, body: any): Observable<any> {
    const url = this.url + '/import/' + floorId;
    return this.http.post(url, body).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Importing CSV File', err))
    );
  }
}
